/* eslint-disable react/no-multi-comp */
/* eslint-disable indent */
import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamTeamSettingsPermissions,
  selectIsActionsBlocked,
  showNotifier
} from 'containers/app/appSlice';
import { Estimate, EstimateTimelineEvent } from '../estimates.model';
import { getEstimateStatusColor, getStatusBGColor } from '../utils';
import { CustomButton, CustomPopover, GenericDeleteModal, GenericError, If } from 'components';
import Lottie from 'lottie-react';
import { LoadingSpinnerAnimation } from 'assets/animations';
import {
  AddIconWoPadding,
  AddNoteIcon,
  ArrowRightIcon,
  CalendarIcon,
  CaretDownIcon,
  ChevronArrowRightIcon,
  EditIcon,
  EditIconBlue,
  InvoicesTabIcon,
  NavCloseIcon,
  WhiteInfoIcon
} from 'assets/icons';
import CustomerSection from '../../jobs/components/CustomerSection';
import LineItemsSection from '../../jobs/components/LineItemsSection';
import { DISCOUNT_TYPE, PopoverArrowTypes } from 'constants/common';
import routesPath from 'routes/RoutesPath';
import { VoidFn } from 'types/baseTypes';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';
import { EstimateStatus, STATUS_CHANGE_ALLOWED_STATUSES, UnlinkSourceType } from '../constants';
import EstimateTimeline from './EstimateTimeline';
import DateUtils from 'utils/dateUtils';
import { useDisconnectJobOrInvoiceFromEstimateMutation, useLazyDownloadEstimateQuery } from '../api';
import { NotifierTypes } from 'constants/notifierConstants';
import { ButtonType } from 'components/custom-button/CustomButton';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import { getInvoiceStatus } from 'containers/invoices/utils';
import { getJobStatusText } from 'containers/jobs/utils';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import ChevronArrowRightReactIcon from 'assets/react-icons/ChevronArrowRightReactIcon';
import { WebAppColors } from 'constants/colors';
import RelatedJobCard from 'containers/jobs/components/RelatedJobCard';
import RelatedInvoiceCard from 'containers/invoices/components/RelatedInvoiceCard';
import { InfoMesssages } from 'types/infoMessages';
import DetailEstimateReminder from './DetailEstimateReminder';
import {
  AddInvoiceAction,
  AddJobAction,
  ArchiveEstimateAction,
  ChangeStatusAction,
  DeleteEstimateAction,
  DuplicateEstimateAction,
  MarkAsAcceptedAction,
  ShareEstimateAction,
  ViewInvoiceAction,
  ViewJobAction
} from './EstimateActions';
import { SubscriptionStatus } from 'containers/app/app.model';
import { differenceInDays, endOfDay, format, isPast, startOfDay } from 'date-fns';
import { getAmountWithCurrency, roundToTwoDecimals } from 'utils/amountUtils';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { getDiscountLabelByTaxTiming } from 'containers/invoices/utils';
import { useCommunicationsControl } from 'containers/communications';

interface EstimateDetailsPanelProps {
  estimate: Estimate;
  onCustomerClick: VoidFn;
  onEditDescriptionClick: VoidFn;
  onEditExpirationClick: VoidFn;
  onEditAssigneesClick: VoidFn;
  getTeamMemberNames: () => JSX.Element | string;
  isRootState: boolean;
  handleSidePanelClose: VoidFn;
  isEstimateByIdFetching: boolean;
  refetchEstimate: (id: string) => void;
  handleDeleteEstimate: VoidFn;
  handleMarkAsExpired: VoidFn;
  handleArchiveEstimate: VoidFn;
  isError: boolean;
  onEditNoteClick: (event: EstimateTimelineEvent) => void;
  onDeleteNoteClick: (event: EstimateTimelineEvent) => void;
  onViewHistoryClick: VoidFn;
  currentActionsPopoverId: number | null;
  setCurrentActionsPopoverId: (id: number | null) => void;
  isSingleMemberTeam: boolean;
  estimateTimeline: EstimateTimelineEvent[];
  onEditStatusClick: VoidFn;
  onEditLeadSourceClick: VoidFn;
  onAddNoteClick: VoidFn;
  onEditTncClick: VoidFn;
  onEditEstimateNoteClick: VoidFn;
  onAttachmentDetailClick: VoidFn;
  handleMarkAsAccepted: VoidFn;
  refetchEstimatesList: VoidFn;
  onEditRemindersClick: VoidFn;
}

const EstimateDetailsPanel: FC<EstimateDetailsPanelProps> = ({
  estimate,
  onCustomerClick,
  onEditExpirationClick,
  onEditAssigneesClick,
  getTeamMemberNames,
  isRootState,
  handleSidePanelClose,
  isEstimateByIdFetching,
  refetchEstimate,
  handleDeleteEstimate,
  handleArchiveEstimate,
  isError,
  onEditNoteClick,
  onDeleteNoteClick,
  onViewHistoryClick,
  currentActionsPopoverId,
  setCurrentActionsPopoverId,
  isSingleMemberTeam,
  estimateTimeline,
  onEditStatusClick,
  onEditLeadSourceClick,
  onAddNoteClick,
  onEditTncClick,
  onEditEstimateNoteClick,
  onAttachmentDetailClick,
  handleMarkAsAccepted,
  refetchEstimatesList,
  onEditRemindersClick
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const manageTeamPermissions = useAppSelector(selectCurrentTeamTeamSettingsPermissions);
  const notifications = useAppSelector(getNotifications);
  const isActionsBlocked = useAppSelector(selectIsActionsBlocked);
  const { isNoPlan } = useFSMPlanInfo();
  const { allowAction: allowInvoiceCreate } = useCommunicationsControl('INVOICE_CREATE');

  const { rootEstimateId, rootJobId, rootInvoiceId, rootReceiptId, rootCustomerId, rootDisputeId, rootPage } =
    useParams();

  const isEditAllowed = estimate && estimate.status.toUpperCase() === EstimateStatus.PENDING;
  const isStatusChangeAllowed =
    estimate &&
    ((estimate?.status.toUpperCase() !== EstimateStatus.INVOICED &&
      STATUS_CHANGE_ALLOWED_STATUSES.includes(estimate.status.toUpperCase() as EstimateStatus)) ||
      (estimate?.status.toUpperCase() === EstimateStatus.INVOICED &&
        (!estimate?.invoices || estimate.invoices?.length === 0)));
  const isLinkingOfInvoiceOrJobAllowed = estimate && estimate.status.toUpperCase() !== EstimateStatus.REJECTED;

  const canDuplicateEstimate = currentTeam?.subscriptionStatus !== SubscriptionStatus.SUBSCRIPTION_EXPIRED;

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<{
    id: string;
    isOpen: boolean;
    isInvoice: boolean;
  }>({
    id: '',
    isOpen: false,
    isInvoice: false
  });

  const [downloadEstimate] = useLazyDownloadEstimateQuery();
  const [disconnectJobOrInvoiceFromEstimate, { isLoading: isDisconnecting }] =
    useDisconnectJobOrInvoiceFromEstimateMutation();

  const handleSidePanelBack = () => {
    if (isRootState) {
      handleSidePanelClose();
    } else {
      navigate(-1);
    }
  };

  const toggleIsMoreOpen = () => {
    setIsMoreOpen(!isMoreOpen);
  };

  const closeMoreOption = () => {
    setIsMoreOpen(false);
  };

  const handleShareEstimate = () => {
    if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/share-estimate/${estimate.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const bottomBar = [
    {
      id: 'estimate-details-bottom-bar-share-button',
      icon: <AddNoteIcon className="h-5 shrink-0" />,
      isDisabled: false,
      disabledReason: '',
      btnText: 'Add note',
      onClickHandler: onAddNoteClick
    },
    estimate?.status?.toUpperCase() === EstimateStatus.INVOICED
      ? {
          ...(estimate.invoices && estimate.invoices.length > 0
            ? {
                id: 'estimate-details-bottom-bar-view-invoice-button',
                icon: <InvoicesTabIcon className="path-stroke-current h-5 shrink-0 text-primary" />,
                isDisabled: false,
                disabledReason: '',
                btnText: 'View invoice',
                onClickHandler: () => {
                  handleInvoiceClick(estimate.invoices[0].id);
                }
              }
            : {
                id: 'estimate-details-bottom-bar-add-invoice-button',
                icon: <InvoicesTabIcon className="path-stroke-current h-5 shrink-0 text-primary" />,
                isDisabled: !isLinkingOfInvoiceOrJobAllowed,
                disabledReason: '',
                btnText: 'Add invoice',
                onClickHandler: () => {
                  onAddInvoiceFromEstimate();
                }
              })
        }
      : {
          id: 'estimate-details-bottom-bar-edit-button',
          icon: <EditIcon className="h-5 shrink-0" />,
          isDisabled: estimate?.status.toUpperCase() !== EstimateStatus.PENDING,
          disabledReason: 'Edit is allowed only for pending estimates',
          btnText: 'Edit',
          onClickHandler: () => {
            navigate(routesPath.ESTIMATES_EDIT.replace(':estimateId', estimate.id));
          }
        }
  ];

  const onAddJobFromEstimate = () => {
    if (isNoPlan) {
      navigate(routesPath.JOBS);
      return;
    }
    if (isLinkingOfInvoiceOrJobAllowed) {
      navigate(`/jobs/create?estimateId=${estimate.id}`);
    } else {
      onInfoClick('linking-job-not-allowed', InfoMesssages.noJobForRejectedEstimate);
    }
  };

  const onAddInvoiceFromEstimate = () => {
    if (!isLinkingOfInvoiceOrJobAllowed) {
      onInfoClick('linking-invoice-not-allowed', InfoMesssages.noInvoiceForRejectedEstimate);
      return;
    }
    if (!allowInvoiceCreate()) return;
    navigate(`/invoices/create?estimateId=${estimate.id}`);
  };

  const handleExportPDF = async () => {
    try {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: 'Downloading started in the background',
            secondaryMessage: '',
            isMessageHtml: false
          },
          type: NotifierTypes.INFO,
          showClose: false,
          fontStyle: 'text-primary font-normal'
        })
      );
      await downloadEstimate({ teamId: currentTeam.id, estimateId: estimate.id });
    } catch (error) {
      // console.error('Failed to export estimate as PDF:', error);
    }
  };

  const PendingEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <MarkAsAcceptedAction
        onMarkAsAccepted={() => {
          handleMarkAsAccepted();
          setCurrentActionsPopoverId(null);
        }}
      />
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {estimate.invoices?.length > 0 ? (
        <ViewInvoiceAction
          onViewInvoice={() => {
            handleInvoiceClick(estimate.invoices[0].id);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddInvoiceAction
          onAddInvoice={() => {
            onAddInvoiceFromEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {estimate.jobs?.length > 0 ? (
        <ViewJobAction
          onViewJob={() => {
            handleJobClick(estimate.jobs[0].jobId);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddJobAction
          onAddJob={() => {
            onAddJobFromEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleArchiveEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {estimate.status.toUpperCase() === EstimateStatus.PENDING && (
        <DeleteEstimateAction
          onDeleteEstimate={() => {
            handleDeleteEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
    </div>
  );

  const AcceptedEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {estimate.invoices?.length > 0 ? (
        <ViewInvoiceAction
          onViewInvoice={() => {
            handleInvoiceClick(estimate.invoices[0].id);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddInvoiceAction
          onAddInvoice={() => {
            onAddInvoiceFromEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {estimate.jobs?.length > 0 ? (
        <ViewJobAction
          onViewJob={() => {
            handleJobClick(estimate.jobs[0].jobId);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddJobAction
          onAddJob={() => {
            onAddJobFromEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleArchiveEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleDeleteEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const RejectedEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleArchiveEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleDeleteEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const ArchivedEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ChangeStatusAction
        onChangeStatus={() => {
          onEditStatusClick();
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleDeleteEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const ExpiredEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleArchiveEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleDeleteEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const InvoicedEstimateActions: FC = () => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {estimate.jobs?.length > 0 ? (
        <ViewJobAction
          onViewJob={() => {
            handleJobClick(estimate.jobs[0].jobId);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddJobAction
          onAddJob={() => {
            onAddJobFromEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ShareEstimateAction
        onShareEstimate={() => {
          handleShareEstimate();
          setCurrentActionsPopoverId(null);
        }}
      />
      {canDuplicateEstimate && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            navigate(routesPath.ESTIMATES_DUPLICATE.replace(':estimateId', estimate.id));
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {(!estimate.invoices || estimate.invoices?.length === 0) && (
        <ArchiveEstimateAction
          onArchiveEstimate={() => {
            handleArchiveEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {(!estimate.invoices || estimate.invoices?.length === 0) && (
        <DeleteEstimateAction
          onDeleteEstimate={() => {
            handleDeleteEstimate();
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
    </div>
  );

  const renderEstimateActions = () => {
    switch (estimate.status.toUpperCase()) {
      case EstimateStatus.PENDING:
        return <PendingEstimateActions />;
      case EstimateStatus.ACCEPTED:
        return <AcceptedEstimateActions />;
      case EstimateStatus.REJECTED:
        return <RejectedEstimateActions />;
      case EstimateStatus.ARCHIVED:
        return <ArchivedEstimateActions />;
      case EstimateStatus.INVOICED:
        return <InvoicedEstimateActions />;
      case EstimateStatus.EXPIRED:
        return <ExpiredEstimateActions />;
    }
  };

  const getSignatureText = () => {
    if (estimate.status.toUpperCase() === EstimateStatus.ACCEPTED) {
      if (estimate.signature.url) {
        return 'Signed';
      }
      return '-';
    }
    return 'Pending';
  };

  const moreAnchorElem = () => (
    <div
      id="estimates-bottom-bar-more-button"
      className="flex shrink grow cursor-pointer items-center justify-center gap-2
      bg-secondary py-3.5 font-semibold text-primary hover:opacity-95">
      <div>More</div>
      <div className="path-stroke-current path">
        <CaretDownIcon className="rotate-180" />
      </div>
    </div>
  );

  const handleOpenRemoveInvoiceConfirmationModal = (invoiceId: string) => {
    setIsConfirmationModalOpen({ id: invoiceId, isOpen: true, isInvoice: true });
  };

  const handleOpenRemoveJobConfirmationModal = (jobId: string) => {
    setIsConfirmationModalOpen({ id: jobId, isOpen: true, isInvoice: false });
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen({ id: '', isOpen: false, isInvoice: false });
  };

  const handleRemoveJobOrInvoiceFromEstimate = async (jobId: string, isInvoice: boolean) => {
    const entityType = isInvoice ? 'INVOICE' : 'JOB';
    try {
      await disconnectJobOrInvoiceFromEstimate({
        teamId: currentTeam.id,
        estimateId: estimate.id,
        entityType,
        entityID: jobId,
        sourceType: UnlinkSourceType.ESTIMATE
      }).unwrap();
      handleCloseConfirmationModal();
      refetchEstimate(estimate.id);
      dispatch(
        showNotifier({
          id: 'estimate-disconnected-from-job',
          message: {
            primaryMessage: `${isInvoice ? 'Invoice' : 'Job'} removed successfully`,
            secondaryMessage: '',
            isMessageHtml: true
          },
          type: NotifierTypes.SUCCESS,
          showClose: true
        })
      );
      if (refetchEstimatesList) {
        refetchEstimatesList();
      }
    } catch (error) {
      //
    }
  };

  const handleJobClick = (jobId: string) => {
    if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleInvoiceClick = (invoiceId: string) => {
    if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/invoice/${invoiceId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleInvoiceShare = invoice => {
    if (invoice.status === InvoiceStatus.PENDING) {
      if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootPage) {
        navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/share-pending-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else {
      if (rootEstimateId) {
        navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootJobId) {
        navigate(`/jobs/${currentTeam.id}/${rootJobId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootInvoiceId) {
        navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam.id}/${rootCustomerId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootPage) {
        navigate(`/payouts/${rootPage}/${currentTeam.id}/${rootPage}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/share-invoice/${invoice.id}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    }
  };

  const onInfoClick = (id, infoMessage) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const formatExpiryDate = (expiryDate: Date) => {
    const todayStart = startOfDay(new Date());
    const expiryEnd = endOfDay(expiryDate);

    if (isPast(expiryEnd)) {
      return `Expired (${format(expiryDate, 'MMM d, yyyy')})`;
    }

    const diffDays = differenceInDays(expiryEnd, todayStart);

    if (diffDays === 0) {
      return `Today (${format(expiryDate, 'MMM d, yyyy')})`;
    }

    return `In ${diffDays} days (${format(expiryDate, 'MMM d, yyyy')})`;
  };

  const showAddInvoiceButton = () => {
    if (estimate?.invoices?.length > 0) {
      return false;
    }
    if (isActionsBlocked) {
      return false;
    }
    return true;
  };

  const calculateDiscount = () => {
    const discount = estimate.discount ? estimate.discount : '0';
    if (!estimate?.lineItems || estimate?.lineItems?.length === 0) {
      return discount;
    }
    if (estimate.discountType === DISCOUNT_TYPE.PERCENTAGE) {
      const lineItemTotal = estimate.lineItems.reduce((acc, lineItem) => {
        const amount = parseFloat(lineItem.amount) || 0;
        return acc + amount;
      }, 0);
      const parsedDiscount = parseFloat(discount) || 0;
      const discountAmount = (parsedDiscount * lineItemTotal) / 100;
      return roundToTwoDecimals(discountAmount).toString();
    }

    return discount;
  };

  const showInvoiceSection = showAddInvoiceButton() || estimate?.invoices?.length > 0;

  const showAddJobButton = () => {
    if (estimate?.jobs?.length > 0) {
      return false;
    }
    if (isActionsBlocked) {
      return false;
    }
    return true;
  };

  const showJobSection = showAddJobButton() || estimate?.jobs?.length > 0;

  return (
    <div className="h-full w-96 max-w-sm">
      {isError ? (
        <GenericError handleErrorBack={handleSidePanelBack} />
      ) : (
        <>
          <If condition={isEstimateByIdFetching}>
            <div className="flex h-full w-96 items-center justify-center">
              <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
            </div>
          </If>
          {estimate && !isEstimateByIdFetching && (
            <div className="flex h-full flex-col">
              <div className="flex items-center gap-2 p-4 pt-5">
                <div onClick={handleSidePanelBack} className="cursor-pointer">
                  {isRootState ? (
                    <NavCloseIcon id="estimate-details-panel-close" />
                  ) : (
                    <ArrowRightIcon className="rotate-180" id="estimate-details-panel-back-arrow" />
                  )}
                </div>
                <div id="estimate-details-panel-head" className="text-17px font-semibold text-headingGray">
                  {estimate.estimateNumber}
                </div>
              </div>
              <div className="customNormalScroll flex grow flex-col overflow-y-auto overflow-x-hidden px-4">
                <div className="flex flex-col items-center justify-center py-6">
                  {estimate.title && (
                    <div className="max-w-[98%] break-words text-center text-[17px] font-bold text-headingGray">
                      {estimate.title}
                    </div>
                  )}
                  {estimate.amount && (
                    <div className="mt-2 text-center text-2xl font-bold text-headingGray">
                      {getAmountWithCurrency(estimate.amount)}
                    </div>
                  )}
                  <div
                    className="mt-2 rounded px-2.5 py-1 capitalize leading-[18px]"
                    style={{
                      backgroundColor: getStatusBGColor(estimate.status as EstimateStatus),
                      color: getEstimateStatusColor(estimate.status as EstimateStatus)
                    }}>
                    {estimate?.status.toLowerCase()}
                  </div>
                  {estimate.status.toUpperCase() === EstimateStatus.REJECTED && estimate.customerComment && (
                    <div
                      className="mt-2.5 max-w-[99%] break-words 
                      rounded bg-blue-2 py-1.5 px-2 text-sbase text-primaryText">
                      Reason: {estimate.customerComment}
                    </div>
                  )}
                  {((estimate.jobs && estimate.jobs.length > 0) ||
                    (estimate.invoices && estimate.invoices.length > 0)) && (
                    <div className="mt-2 w-full space-y-2 border-t border-secondaryBtn pt-2">
                      {estimate?.invoices &&
                        estimate.invoices?.length > 0 &&
                        estimate.invoices?.map(invoice => (
                          <div
                            key={`invoice-${invoice.id}`}
                            className="flex cursor-pointer items-center justify-center rounded-md px-2"
                            onClick={() => handleInvoiceClick(invoice.id)}
                            tabIndex={0}>
                            <span className="text-sbase font-semibold text-primaryText">{invoice.invoiceNumber}</span>
                            <span className="px-1.5 text-[10px] text-accentText">•</span>
                            <div className="flex items-center gap-2 text-accentText">
                              <span className="text-sbase">
                                Invoice {getInvoiceStatus(invoice.status as InvoiceStatus)?.toLowerCase()}
                              </span>
                              <ChevronArrowRightIcon className="path-fill-current path h-4 w-4" />
                            </div>
                          </div>
                        ))}
                      {estimate?.jobs &&
                        estimate.jobs?.length > 0 &&
                        estimate.jobs?.map(job => (
                          <div
                            key={`job-${job.jobId}`}
                            className="flex cursor-pointer items-center justify-center rounded-md px-2"
                            onClick={() => handleJobClick(job.jobId)}
                            tabIndex={0}>
                            <span className="text-sbase font-semibold text-primaryText">
                              {job.jobTitle || job.jobNumber || ''}
                            </span>
                            <span className="px-1.5 text-[10px] text-accentText">•</span>
                            <div className="flex items-center gap-2 text-accentText">
                              <span className="text-sbase">
                                Job {job.jobStatus ? getJobStatusText(job.jobStatus)?.toLowerCase() : ''}
                              </span>
                              <ChevronArrowRightIcon className="path-fill-current path h-4 w-4" />
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-4">
                  <CustomerSection customer={estimate.customer} onCustomerClick={onCustomerClick} />

                  <div className="flex flex-col gap-y-2">
                    <div className="text-sbase font-bold text-primaryText">Status</div>
                    <div
                      className={`flex ${
                        isStatusChangeAllowed ? 'cursor-pointer' : 'pointer-events-none'
                      } items-center justify-between gap-x-3 rounded-md 
                      border border-borderGray p-3 pr-4`}
                      role="presentation"
                      onClick={onEditStatusClick}>
                      <div className="flex items-center gap-x-2">
                        <div
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: getEstimateStatusColor(estimate.status as EstimateStatus) }}
                        />
                        <div
                          className="text-sbase font-semibold capitalize"
                          style={{ color: getEstimateStatusColor(estimate.status as EstimateStatus) }}>
                          {estimate.status.toLowerCase()}
                        </div>
                      </div>
                      {isStatusChangeAllowed && (
                        <ChevronArrowRightReactIcon
                          id="estimate-detail-status-edit-btn"
                          className="shrink-0 cursor-pointer"
                          onClick={() => null}
                          fill={WebAppColors.Gray_1}
                        />
                      )}
                    </div>
                  </div>
                  <EstimateTimeline
                    estimateTimeline={estimateTimeline}
                    limit={3}
                    onEditNoteClick={onEditNoteClick}
                    onDeleteNoteClick={onDeleteNoteClick}
                    hasImageViewer={false}
                    timelineId="estimate-details-timeline"
                    currentActionsPopoverId={currentActionsPopoverId}
                    setCurrentActionsPopoverId={setCurrentActionsPopoverId}
                    onViewHistoryClick={onViewHistoryClick}
                    isEditAllowed={isEditAllowed}
                  />

                  {estimate.lineItems && (
                    <LineItemsSection
                      lineItems={estimate.lineItems}
                      totalAmount={estimate.payableAmount}
                      discount={calculateDiscount()}
                      payableAmount={estimate.billableAmount}
                      discountLabel={getDiscountLabelByTaxTiming(estimate.discountTaxTiming)}
                    />
                  )}

                  <div className="flex flex-col gap-y-2">
                    <div className="text-sbase font-bold text-primaryText">Expires on</div>
                    <div
                      className={`flex
                        items-center justify-between gap-x-3 rounded-md border border-borderGray p-3 pr-4 ${
                          isEditAllowed ? 'cursor-pointer' : 'pointer-events-none'
                        }`}
                      role="presentation"
                      onClick={onEditExpirationClick}>
                      <CalendarIcon className="shrink-0" />
                      <div className="flex w-full justify-between text-sbase font-semibold text-primaryBtn">
                        <div>
                          {estimate.hasExpiry
                            ? formatExpiryDate(DateUtils.parseISODateString(estimate.expiresAt))
                            : 'No expiry'}
                        </div>
                      </div>
                      {isEditAllowed && (
                        <EditIconBlue
                          className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                          onClick={onEditExpirationClick}
                          role="presentation"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-y-2">
                    <div className="text-sbase font-bold text-primaryText">{`Sold by (${
                      estimate.assignees?.length ?? 0
                    })`}</div>
                    <div
                      className="flex items-center justify-between gap-x-3 rounded-md border border-borderGray p-3 pr-4"
                      role="presentation">
                      <div className="text-sbase font-semibold text-primaryText">{getTeamMemberNames()}</div>
                      {(manageTeamPermissions.editExistingMember || manageTeamPermissions.viewExistingMember) &&
                        !isSingleMemberTeam &&
                        isEditAllowed && (
                          <EditIconBlue
                            className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                            onClick={onEditAssigneesClick}
                            role="presentation"
                          />
                        )}
                    </div>
                  </div>
                  {estimate?.reminderEnabled && estimate?.hasExpiry && (
                    <DetailEstimateReminder
                      reminders={estimate?.reminders}
                      isEditAllowed={isEditAllowed}
                      onEditClick={onEditRemindersClick}
                    />
                  )}
                  {estimate?.note && (
                    <div className="flex flex-col gap-y-2">
                      <div className="text-sbase font-bold text-primaryText">Note</div>
                      <div className="rounded-md border border-borderGray p-3 pr-4">
                        <div className="flex items-start justify-between">
                          <div
                            className="text-ellipsis whitespace-pre-wrap break-words text-sbase 
                                      font-semibold text-primaryText line-clamp-3">
                            {estimate.note}
                          </div>
                          {isEditAllowed && (
                            <EditIconBlue
                              className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                              onClick={onEditEstimateNoteClick}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {estimate?.attachments?.length && (
                    <div className="flex flex-col gap-y-2">
                      <div className="flex justify-between">
                        <div className="text-sbase font-bold text-primaryText">Attachments</div>
                        <div
                          className="cursor-pointer text-sbase font-semibold text-primaryBtn"
                          onClick={onAttachmentDetailClick}>
                          View Details
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-4">
                        {estimate.attachments.map(
                          (attachment, attachmentIndex) =>
                            attachment?.urlLink && (
                              <div
                                key={`estimate-attachment-${attachmentIndex}`}
                                className="relative h-[60px] max-h-[60px] w-[60px]">
                                <img
                                  src={attachment.urlLink}
                                  height={60}
                                  width={60}
                                  className="h-[60px] max-h-[60px] w-[60px] rounded object-cover"
                                />
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  )}
                  {(estimate.tncEdited || estimate.tnc) && (
                    <div className="flex flex-col gap-y-2">
                      <div className="text-sbase font-bold text-primaryText">Terms & Conditions</div>
                      <div className="rounded-md border border-borderGray p-3 pr-4">
                        {estimate.tnc ? (
                          <div className="flex items-start justify-between">
                            <div
                              className="text-ellipsis whitespace-pre-wrap break-words text-sbase 
                                      font-semibold text-primaryText line-clamp-3">
                              {estimate.tnc}
                            </div>
                            {isEditAllowed && (
                              <EditIconBlue
                                className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                                onClick={onEditTncClick}
                              />
                            )}
                          </div>
                        ) : (
                          <div
                            className={`flex items-center ${
                              isEditAllowed ? 'cursor-pointer' : 'pointer-events-none opacity-50'
                            }`}
                            role="presentation"
                            onClick={onEditTncClick}>
                            <AddIconWoPadding className="mr-2 shrink-0" />
                            <div className="text-sbase font-semibold text-primaryBtn"> Add Terms & Conditions </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {showInvoiceSection && (
                    <div>
                      <div className="pb-4 text-sbase font-semibold text-primaryText">Invoice</div>
                      {estimate.invoices &&
                        estimate.invoices.length > 0 &&
                        estimate.invoices.map(invoice => (
                          <div key={invoice.id} className="w-full">
                            <RelatedInvoiceCard
                              invoice={invoice}
                              onInvoiceClick={handleInvoiceClick}
                              onShareInvoice={handleInvoiceShare}
                              onRemoveInvoice={handleOpenRemoveInvoiceConfirmationModal}
                            />
                          </div>
                        ))}
                      {showAddInvoiceButton() && (
                        <CustomButton
                          showDisabledReason={!isLinkingOfInvoiceOrJobAllowed}
                          id="add-invoice-from-estimate"
                          type={ButtonType.SECONDARY}
                          className="h-10 w-full border-borderGray"
                          onClick={onAddInvoiceFromEstimate}>
                          <span className="text-xl">+ </span>
                          {'  '}Add invoice
                        </CustomButton>
                      )}
                    </div>
                  )}

                  {showJobSection && (
                    <div>
                      <div className="pb-4 text-sbase font-semibold text-primaryText">Job</div>
                      {estimate.jobs?.length > 0 &&
                        estimate.jobs.map(job => (
                          <RelatedJobCard
                            key={job.jobId}
                            job={job}
                            onJobClick={handleJobClick}
                            onRemoveJob={handleOpenRemoveJobConfirmationModal}
                            className="rounded-md p-3"
                          />
                        ))}
                      {showAddJobButton() && (
                        <CustomButton
                          showDisabledReason={!isLinkingOfInvoiceOrJobAllowed}
                          id="add-job-from-estimate"
                          type={ButtonType.SECONDARY}
                          className="h-10 w-full border-borderGray"
                          onClick={onAddJobFromEstimate}>
                          <span className="text-xl">+ </span>
                          {'  '}Add job
                        </CustomButton>
                      )}
                    </div>
                  )}

                  {/* <div className="flex flex-col gap-y-2">
                    <div className="text-sbase font-bold text-primaryText">Estimate description</div>
                    <div className="rounded-md border border-borderGray p-3 pr-4">
                      {estimate.description ? (
                        <div className="flex items-start justify-between ">
                          <div
                            className="text-ellipsis whitespace-pre-wrap break-words text-sbase font-semibold 
                                      text-primaryText line-clamp-3">
                            {estimate.description}
                          </div>
                          {isEditAllowed && (
                            <EditIconBlue
                              className="h-[18px] w-[18px] shrink-0 cursor-pointer"
                              onClick={onEditDescriptionClick}
                            />
                          )}
                        </div>
                      ) : (
                        <div
                          className={`flex items-center ${
                            isEditAllowed ? 'cursor-pointer' : 'pointer-events-none opacity-50'
                          }`}
                          role="presentation"
                          onClick={onEditDescriptionClick}>
                          <AddIconWoPadding className="mr-2 shrink-0" />
                          <div className="text-sbase font-semibold text-primaryBtn"> Add a description </div>
                        </div>
                      )}
                    </div>
                  </div> */}

                  <div className="flex flex-col gap-y-2">
                    <div className="text-sbase font-bold text-primaryText">Lead source</div>
                    <div
                      className={`flex items-center justify-between gap-x-3 rounded-md border 
                      border-borderGray p-3 pr-4 ${isEditAllowed ? 'cursor-pointer' : 'pointer-events-none'}`}
                      role="presentation"
                      onClick={onEditLeadSourceClick}>
                      {estimate.leadSourceType ? (
                        <>
                          <div className="text-sbase font-semibold capitalize text-primaryText">
                            {estimate.leadSourceType} {estimate.leadSource ? `- ${estimate.leadSource}` : ''}
                          </div>
                          {isEditAllowed && (
                            <ChevronArrowRightReactIcon
                              id="estimate-detail-lead-source-edit-btn"
                              className="shrink-0 cursor-pointer"
                              fill={WebAppColors.Gray_1}
                              onClick={onEditLeadSourceClick}
                              role="presentation"
                            />
                          )}
                        </>
                      ) : (
                        <div
                          className={`flex items-center gap-x-2 ${
                            isEditAllowed ? 'cursor-pointer' : 'pointer-events-none opacity-50'
                          }`}
                          role="presentation"
                          onClick={onEditLeadSourceClick}>
                          <AddIconWoPadding className="mr-2 shrink-0" />
                          <div className="text-sbase font-semibold text-primaryBtn">Add lead source</div>
                        </div>
                      )}
                    </div>
                  </div>
                  {estimate.isSignatureEnabled && (
                    <div className="flex flex-col gap-y-2">
                      <div className="text-sbase font-bold text-primaryText">Customer signature</div>
                      <div
                        className="flex cursor-pointer flex-col gap-5 
                      rounded-md border border-borderGray bg-primary p-3">
                        <div className="flex justify-between gap-1 text-sbase font-semibold">
                          <div id="estimate-panel-customer-signature" className="truncate text-accentText">
                            Status
                          </div>
                          <div id="estimate-panel-customer-sign-status" className="truncate text-tertiaryText">
                            {getSignatureText()}
                          </div>
                        </div>
                        <If condition={Boolean(estimate.signature.customerName)}>
                          <div className="flex justify-between gap-1 text-sbase font-semibold text-tertiaryText">
                            <div id="estimate-panel-customer-signature-cust-name" className="truncate">
                              {estimate.signature.customerName}
                            </div>
                            <div id="estimate-panel-customer-signature-date" className="shrink-0">
                              {DateUtils.getDateInFormatwithTimezone({
                                date: new Date(estimate.signature.signedAt),
                                dateFormat: 'dd MMM yyyy, h:mm a'
                              })}
                            </div>
                          </div>
                        </If>
                      </div>
                    </div>
                  )}
                  <div className="flex w-full flex-col gap-y-4 pb-4">
                    <div className="text-sbase font-bold text-primaryText">Additional info</div>
                    <div className="flex items-center justify-between">
                      <div className="text-sbase font-semibold text-accentText">Estimate number</div>
                      <div className="text-right text-sbase font-semibold text-tertiaryText">
                        {estimate.estimateNumber}
                      </div>
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="text-sbase font-semibold text-accentText">Reference ID</div>
                      <div className="w-1/2 break-words text-right text-sbase font-semibold text-tertiaryText">
                        {estimate.referenceNo || '-'}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sbase font-semibold text-accentText">Created on</div>
                      <div className="text-right text-sbase font-semibold text-tertiaryText">
                        {DateUtils.getDateInFormat({ date: new Date(estimate.createdAt), dateFormat: 'dd MMM yyyy' })}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sbase font-semibold text-accentText">Created by</div>
                      <div className="text-right text-sbase font-semibold text-tertiaryText">
                        {estimate.merchantName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-auto flex justify-between gap-px justify-self-end">
                {bottomBar.map(item => (
                  <div
                    id={`estimate-side-panel-${item.btnText}-button`}
                    onClick={() => {
                      if (item.isDisabled) {
                        onInfoClick(item.id, item.disabledReason);
                      } else {
                        item.onClickHandler();
                      }
                    }}
                    key={item.id + item.btnText}
                    className={twMerge(
                      classNames(
                        `text-primary bg-secondary flex gap-2 py-3.5 grow
                           justify-center items-center font-semibold cursor-pointer hover:opacity-95`,
                        item.isDisabled ? 'bg-[#9BBADD] hover:opacity-100' : 'shrink items-center'
                      )
                    )}>
                    <div className={`${item.isDisabled && 'opacity-50'}`}>{item.icon}</div>
                    <div className={`text-sbase ${item.isDisabled && 'opacity-50'}`}>{item.btnText}</div>
                    {item.isDisabled && <WhiteInfoIcon />}
                  </div>
                ))}
                <div
                  className="flex shrink grow cursor-pointer items-center justify-center
                               gap-2 bg-secondary hover:opacity-95"
                  onClick={toggleIsMoreOpen}>
                  <CustomPopover
                    show={isMoreOpen}
                    anchorComponent={moreAnchorElem}
                    onTogglePopover={toggleIsMoreOpen}
                    onClosePopover={closeMoreOption}
                    offset="-right-3 bottom-14"
                    minWidth="min-w-[200px]"
                    showArrow={true}
                    arrowType={PopoverArrowTypes.BOTTOM}
                    highlightAnchor={false}
                    showOnTop={true}>
                    {renderEstimateActions()}
                  </CustomPopover>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {isConfirmationModalOpen.isOpen && (
        <GenericDeleteModal
          isActionInProgress={isDisconnecting}
          primaryBtnId="remove-job-from-estimate"
          primaryBtnLabel="Remove"
          secondaryBtnId="cancel-remove-job-from-estimate"
          secondaryBtnLabel="Cancel"
          onPrimaryBtnClick={() =>
            handleRemoveJobOrInvoiceFromEstimate(isConfirmationModalOpen.id, isConfirmationModalOpen.isInvoice)
          }
          onSecondaryBtnClick={handleCloseConfirmationModal}
          showModal={true}
          title={isConfirmationModalOpen.isInvoice ? 'Remove invoice' : 'Remove job'}
          subTitle={
            isConfirmationModalOpen.isInvoice
              ? 'Are you sure you want to remove invoice from estimate?'
              : 'Are you sure you want to remove job from estimate?'
          }
        />
      )}
    </div>
  );
};

export default EstimateDetailsPanel;
